import { Wand2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { loadSampleFile } from "@/lib/sample";

interface SampleButtonProps {
  /**
   * Receives the fetched sample File and runs the host page's normal flow
   * (convert on /app, analyze on /layout). May be async; this component shows
   * its own "loading" state only for the brief fetch, then hands off.
   */
  onSample: (file: File) => void | Promise<void>;
  /** Disable while the host page is already busy (converting / analyzing). */
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "ghost" | "link";
  label?: string;
  className?: string;
}

export function SampleButton({
  onSample,
  disabled,
  size = "md",
  variant = "secondary",
  label = "サンプルで試す",
  className,
}: SampleButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setError(null);
    setLoading(true);
    try {
      await onSample(await loadSampleFile());
    } catch (e) {
      setError(e instanceof Error ? e.message : "サンプルの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={className}>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={disabled || loading}
        title="用意されたサンプル資料でこのアプリの機能を試します"
      >
        <Wand2 size={16} />
        {loading ? "サンプルを読み込み中…" : label}
      </Button>
      {error && <p className="mt-2 text-sm text-[var(--color-coral-dark)]">{error}</p>}
    </div>
  );
}
